import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { BarChart3, Globe, Hash, Images, RefreshCw, Tag, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { EmptyState, ErrorState } from '@/components/states';
import { MonthlyChart } from '@/components/charts/monthly-chart';
import { Sparkline } from '@/components/charts/sparkline';
import { useStatsQuery } from '@/hooks/use-stats';
import { useCategoryLabels } from '@/hooks/use-categories';
import { countForMonth, cumulate, fillMonthGaps, formatMonth } from '@/lib/activity';
import { formatShare, pluralize } from '@/lib/format';
import type { ActivityPoint, RankedFacet } from '@/types/api';

/**
 * Insights page. Behind sign-in — see RequireSignedIn in app.tsx and requireAuthenticated on the
 * API, because every figure here comes from who shared what and where.
 *
 * The wording is deliberate everywhere: SLC keeps ONE record per unique link, owned by its first
 * sharer, with no later-share history. So a person's number is "unique links they first shared",
 * a channel's is "unique links first shared there", and neither is a share count. The copy says
 * "first shared" rather than "shared" so the page can never be misread as a leaderboard of volume.
 */

function StatCard({
  icon: Icon,
  label,
  value,
  detail,
  trend,
}: {
  icon: typeof Hash;
  label: string;
  value: number;
  /** One quiet fact under the value, e.g. "+6 this month". */
  detail?: string;
  /** Recent monthly series drawn as a sparkline beside the value. */
  trend?: ActivityPoint[];
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <Icon aria-hidden="true" className="h-4 w-4" />
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex items-end justify-between gap-3">
        <div className="min-w-0">
          {/* Display-size values keep proportional figures; tabular digits read loose this large. */}
          <p className="text-3xl font-semibold">{value.toLocaleString()}</p>
          {detail ? <p className="mt-1 truncate text-xs text-muted-foreground">{detail}</p> : null}
        </div>
        {trend ? <Sparkline points={trend} /> : null}
      </CardContent>
    </Card>
  );
}

/**
 * A horizontal bar ranking. Each row links into the catalogue (or the Media page) filtered to
 * that value, so the page is a jumping-off point rather than a dead end. One hue for every bar
 * on purpose: the categories are nominal, so bar length carries the magnitude and color stays out
 * of it.
 */
function RankedList({
  title,
  description,
  icon: Icon,
  items,
  unit,
  total,
  hrefFor,
  destination = 'the catalogue',
  labelFor = (item) => item.label,
  emptyLabel,
}: {
  title: string;
  description: string;
  icon: typeof Hash;
  items: RankedFacet[];
  /** Noun for the count, e.g. "link". Pluralized automatically. */
  unit: string;
  /** When set, each row also shows its share of this figure. */
  total?: number;
  hrefFor: (item: RankedFacet) => string;
  /** Where a row's link leads, for its hover text. */
  destination?: string;
  labelFor?: (item: RankedFacet) => string;
  emptyLabel: string;
}) {
  // Scale bars against the top entry so the ranking is legible regardless of absolute magnitude.
  const max = items.reduce((peak, item) => Math.max(peak, item.linkCount), 0);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Icon aria-hidden="true" className="h-4 w-4 text-muted-foreground" />
          {title}
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">{emptyLabel}</p>
        ) : (
          <ol className="flex flex-col gap-1">
            {items.map((item, index) => {
              const label = labelFor(item);
              const width = max > 0 ? Math.max((item.linkCount / max) * 100, 4) : 0;
              const share = total ? formatShare(item.linkCount, total) : '';
              return (
                <li key={item.value}>
                  <Link
                    to={hrefFor(item)}
                    className="group flex items-center gap-3 rounded-md px-2 py-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    title={`${label} — ${item.linkCount} ${pluralize(item.linkCount, unit)}${share ? ` (${share} of ${unit}s)` : ''}. View in ${destination}.`}
                  >
                    <span className="w-4 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                      {index + 1}
                    </span>
                    <span className="relative flex-1 overflow-hidden">
                      {/* The bar sits behind the label so long names stay readable. */}
                      <span
                        aria-hidden="true"
                        className="absolute inset-y-0 left-0 rounded bg-primary/15 transition-colors group-hover:bg-primary/25"
                        style={{ width: `${width}%` }}
                      />
                      <span className="relative block truncate px-2 py-1 text-sm font-medium">
                        {label}
                      </span>
                    </span>
                    <span className="flex shrink-0 items-baseline gap-2">
                      <span className="text-sm font-medium tabular-nums">
                        {item.linkCount.toLocaleString()}
                      </span>
                      {share ? (
                        <span className="w-9 text-right text-xs tabular-nums text-muted-foreground">
                          {share}
                        </span>
                      ) : null}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Monthly activity: per-month columns and the cumulative running total as two tabs over the same
 * series, plus a table view so no value is gated behind a hover.
 */
function ActivityCard({
  title,
  unit,
  emptyLabel,
  filled,
  totalCounted,
  truncated,
}: {
  title: string;
  /** What is counted, singular: "link", or "file" for images and videos together. */
  unit: 'link' | 'file';
  emptyLabel: string;
  /** The month series with its gaps filled — see fillMonthGaps. */
  filled: ActivityPoint[];
  totalCounted: number;
  truncated: boolean;
}) {
  const cumulativePoints = useMemo(() => cumulate(filled), [filled]);

  const peak = filled.reduce((best, point) => (point.count > best.count ? point : best), {
    period: '',
    count: 0,
  });
  const average = filled.length > 0 ? Math.round(totalCounted / filled.length) : 0;

  const tableRows = useMemo(
    () =>
      filled
        .map((point, index) => ({
          period: point.period,
          count: point.count,
          runningTotal: cumulativePoints[index].count,
        }))
        .reverse(),
    [filled, cumulativePoints],
  );

  if (filled.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <BarChart3 aria-hidden="true" className="h-4 w-4 text-muted-foreground" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="py-6 text-center text-sm text-muted-foreground">{emptyLabel}</p>
        </CardContent>
      </Card>
    );
  }

  const first = filled[0];
  const last = filled[filled.length - 1];

  return (
    <Card>
      <Tabs defaultValue="monthly">
        <CardHeader className="flex flex-col gap-3 pb-2 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex flex-col gap-1">
            <CardTitle className="flex items-center gap-2 text-base">
              <BarChart3 aria-hidden="true" className="h-4 w-4 text-muted-foreground" />
              {title}
            </CardTitle>
            <CardDescription>
              Peak {formatMonth(peak.period)} ({peak.count.toLocaleString()}) · about{' '}
              {average.toLocaleString()} per month
            </CardDescription>
          </div>
          <TabsList>
            <TabsTrigger value="monthly">Monthly</TabsTrigger>
            <TabsTrigger value="cumulative">Cumulative</TabsTrigger>
          </TabsList>
        </CardHeader>
        <CardContent>
          {truncated ? (
            <p className="mb-2 text-xs text-muted-foreground">
              The catalogue is large enough that the earliest months are undercounted.
            </p>
          ) : null}
          <TabsContent value="monthly" className="mt-1">
            <MonthlyChart
              points={filled}
              variant="columns"
              unit={unit}
              ariaLabel={`Unique ${unit}s first shared per month, ${formatMonth(first.period)} to ${formatMonth(last.period)}`}
            />
          </TabsContent>
          <TabsContent value="cumulative" className="mt-1">
            <MonthlyChart
              points={cumulativePoints}
              variant="cumulative"
              unit={unit}
              ariaLabel={`Running total of unique ${unit}s at the end of each month`}
            />
          </TabsContent>

          <details className="mt-3 border-t border-border pt-3">
            <summary className="cursor-pointer select-none text-xs font-medium text-muted-foreground transition-colors hover:text-foreground">
              View as table
            </summary>
            <div className="mt-3 max-h-64 overflow-y-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-muted-foreground">
                    <th className="pb-2 text-left font-medium">Month</th>
                    <th className="pb-2 text-right font-medium">First shared</th>
                    <th className="pb-2 text-right font-medium">Running total</th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((row) => (
                    <tr key={row.period} className="border-t border-border/60">
                      <td className="py-1.5">{formatMonth(row.period)}</td>
                      <td className="py-1.5 text-right tabular-nums">
                        {row.count.toLocaleString()}
                      </td>
                      <td className="py-1.5 text-right tabular-nums">
                        {row.runningTotal.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </CardContent>
      </Tabs>
    </Card>
  );
}

export function StatsPage() {
  const stats = useStatsQuery(true);
  const categoryLabels = useCategoryLabels();

  const topCategories = useMemo(
    () =>
      (stats.data?.topCategories ?? []).map((item) => ({
        ...item,
        // Facet labels for categories are slugs; show the human name when we know it.
        label: categoryLabels.get(item.value) ?? item.label,
      })),
    [stats.data?.topCategories, categoryLabels],
  );

  const currentMonth = format(new Date(), 'yyyy-MM');

  const activityPoints = stats.data?.activity.points;
  const filledPoints = useMemo(() => fillMonthGaps(activityPoints ?? []), [activityPoints]);
  const thisMonth = countForMonth(filledPoints, currentMonth);
  const trend = filledPoints.slice(-12);

  const mediaPoints = stats.data?.mediaActivity.points;
  const filledMediaPoints = useMemo(() => fillMonthGaps(mediaPoints ?? []), [mediaPoints]);
  const mediaTrend = filledMediaPoints.slice(-12);

  const isEmpty = stats.data
    ? stats.data.totals.links === 0 && stats.data.totals.media === 0
    : false;

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight">Statistics</h1>
          <p className="max-w-2xl text-sm text-muted-foreground">
            Every figure counts <strong>unique links</strong> or <strong>distinct files</strong>{' '}
            (images and videos), each owned by whoever shared it first — SLC keeps no later-share
            history, so these are firsts, not totals.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          loading={stats.isFetching}
          onClick={() => void stats.refetch()}
        >
          <RefreshCw aria-hidden="true" className="h-4 w-4" />
          Refresh
        </Button>
      </header>

      <p role="status" aria-live="polite" className="sr-only">
        {stats.isPending
          ? 'Loading statistics'
          : stats.isError
            ? 'Could not load statistics.'
            : `${stats.data.totals.links} unique ${pluralize(stats.data.totals.links, 'link')}, ${stats.data.totals.images} ${pluralize(stats.data.totals.images, 'image')} and ${stats.data.totals.videos} ${pluralize(stats.data.totals.videos, 'video')} across ${stats.data.totals.channels} ${pluralize(stats.data.totals.channels, 'channel')}.`}
      </p>

      {stats.isError ? (
        <ErrorState
          title="Could not load statistics"
          error={stats.error}
          onRetry={() => void stats.refetch()}
        />
      ) : stats.isPending ? (
        <div className="flex flex-col gap-6" aria-hidden="true">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
            {[0, 1, 2, 3, 4, 5].map((index) => (
              <Skeleton key={index} className="h-24 w-full" />
            ))}
          </div>
          <Skeleton className="h-72 w-full" />
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {[0, 1, 2, 3].map((index) => (
              <Skeleton key={index} className="h-72 w-full" />
            ))}
          </div>
        </div>
      ) : isEmpty ? (
        <EmptyState
          icon={<BarChart3 aria-hidden="true" className="h-8 w-8" />}
          title="No statistics yet"
          description="Once links, images and videos start being collected, this page will show the busiest channels, the most active sharers and how activity trends over time."
        />
      ) : (
        <>
          <section aria-label="Totals">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
              <StatCard
                icon={BarChart3}
                label="Unique links"
                value={stats.data.totals.links}
                detail={thisMonth > 0 ? `+${thisMonth.toLocaleString()} this month` : undefined}
                trend={trend}
              />
              <StatCard
                icon={Images}
                label="Images & videos"
                value={stats.data.totals.media}
                detail={
                  stats.data.totals.media > 0
                    ? `${stats.data.totals.images.toLocaleString()} ${pluralize(stats.data.totals.images, 'image')} · ${stats.data.totals.videos.toLocaleString()} ${pluralize(stats.data.totals.videos, 'video')}`
                    : undefined
                }
                trend={mediaTrend}
              />
              <StatCard icon={Hash} label="Channels" value={stats.data.totals.channels} />
              <StatCard icon={Users} label="Sharers" value={stats.data.totals.sharers} />
              <StatCard icon={Globe} label="Domains" value={stats.data.totals.domains} />
              <StatCard
                icon={Tag}
                label="Categories in use"
                value={stats.data.totals.categoriesInUse}
              />
            </div>
          </section>

          <ActivityCard
            title="Links first shared over time"
            unit="link"
            emptyLabel="No links to chart yet."
            filled={filledPoints}
            totalCounted={stats.data.activity.totalCounted}
            truncated={stats.data.activity.truncated}
          />

          <ActivityCard
            title="Images and videos first shared over time"
            unit="file"
            emptyLabel="No images or videos collected yet. An administrator chooses which channels to collect them from in Settings."
            filled={filledMediaPoints}
            totalCounted={stats.data.mediaActivity.totalCounted}
            truncated={stats.data.mediaActivity.truncated}
          />

          <section aria-label="Rankings" className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <RankedList
              title="Busiest channels"
              description="Where new links surface first."
              icon={Hash}
              items={stats.data.topChannels}
              unit="link"
              total={stats.data.totals.links}
              hrefFor={(item) => `/?channel=${encodeURIComponent(item.value)}`}
              emptyLabel="No channels yet."
            />
            <RankedList
              title="Most active sharers"
              description="Who brings in the most new links."
              icon={Users}
              items={stats.data.topSharers}
              unit="link"
              total={stats.data.totals.links}
              hrefFor={(item) => `/?sharer=${encodeURIComponent(item.value)}`}
              emptyLabel="No sharers yet."
            />
            <RankedList
              title="Top domains"
              description="Where shared links point."
              icon={Globe}
              items={stats.data.topDomains}
              unit="link"
              total={stats.data.totals.links}
              hrefFor={(item) => `/?domain=${encodeURIComponent(item.value)}`}
              emptyLabel="No domains yet."
            />
            <RankedList
              title="Top categories"
              description="What the catalogue is about. Links can carry several categories."
              icon={Tag}
              items={topCategories}
              unit="link"
              total={stats.data.totals.links}
              hrefFor={(item) => `/?category=${encodeURIComponent(item.value)}`}
              emptyLabel="No categories in use yet."
            />
            <RankedList
              title="Busiest channels for images and videos"
              description="Among the channels they are collected from."
              icon={Images}
              items={stats.data.topMediaChannels}
              unit="file"
              total={stats.data.totals.media}
              hrefFor={(item) => `/media?channel=${encodeURIComponent(item.value)}`}
              destination="the Media page"
              emptyLabel="No images or videos collected yet."
            />
            <RankedList
              title="Top image and video sharers"
              description="Who posts the most new images and videos."
              icon={Users}
              items={stats.data.topMediaSharers}
              unit="file"
              total={stats.data.totals.media}
              hrefFor={(item) => `/media?sharer=${encodeURIComponent(item.value)}`}
              destination="the Media page"
              emptyLabel="No images or videos collected yet."
            />
          </section>
        </>
      )}
    </div>
  );
}
